import { useCallback, useEffect, useRef } from 'react';

import { useMutation } from '@tanstack/react-query';

import { loginStateService } from '@/state/login.state.service';

import { API_ROUTES } from './apiRoutes';
import { isStaleSessionAuthFailure } from './sessionAuthFailure.utils';
import useAxios from './useAxios';

const { post } = useAxios();

export const useKeepAlive = (logout: () => void) => {
  const isLoggingOutRef = useRef(false);
  const logoutRef = useRef(logout);
  logoutRef.current = logout;

  const keepAlive = useMutation({
    mutationFn: async () => {
      return await post(API_ROUTES.KEEP_ALIVE, { data: {}, headers: {} });
    },
  });

  const handleError = useCallback((error: unknown) => {
    if (!loginStateService.isLoggedIn.get()) return;
    if (isLoggingOutRef.current) return;
    if (isStaleSessionAuthFailure(error)) return;
    isLoggingOutRef.current = true;
    logoutRef.current();
  }, []);

  const checkKeepAlive = useCallback(() => {
    if (!loginStateService.isLoggedIn.get()) return;
    keepAlive.mutateAsync().catch(handleError);
  }, [keepAlive, handleError]);

  useEffect(() => {
    window.addEventListener('focus', checkKeepAlive);
    return () => window.removeEventListener('focus', checkKeepAlive);
  }, [checkKeepAlive]);

  useEffect(() => {
    if (keepAlive.isIdle) {
      checkKeepAlive();
    }
  }, [keepAlive.isSuccess]);
};
